import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, Observable, catchError, concatMap, from, map, tap, throwError, toArray } from 'rxjs';
import { SelectionListDefault, SelectionListKey, SelectionListWert } from '../../models/selection-list-default.model';
import { SelectionListDefaultService } from '../../services/selection-list-default.service';
import {
  AuswahllistenEditorComponent,
  FeldFehler,
  WerteAenderung,
  gleicheWerte,
  listeGueltig,
} from '../auswahllisten-editor/auswahllisten-editor.component';
import { VerwaltungBereich } from './verwaltung-bereich';
import { LadeZustand, VerwaltungStore } from './verwaltung.store';
import { VwUngespeichertComponent } from './vw-ungespeichert.component';

const FELD_INDEX = /^werte\[(\d+)\]/;

class Abgelehnt {}

@Component({
  selector: 'app-vw-auswahllisten',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuswahllistenEditorComponent, TranslatePipe, VwUngespeichertComponent],
  template: `
    @if (zustand() === 'fehler') {
      <div class="vw-auswahllisten__fehler" role="alert" data-testid="aw-ladefehler">
        <span>{{ 'auswahllisten.ladefehler' | translate }}</span>
        <button type="button" class="vw-auswahllisten__erneut" data-testid="aw-erneut" (click)="laden()">
          {{ 'auswahllisten.erneut' | translate }}
        </button>
      </div>
    } @else {
      <app-auswahllisten-editor
        modus="mandant"
        [listen]="listen()"
        [laedt]="zustand() === 'laedt'"
        [feldFehler]="feldFehler()"
        (werteChange)="werteAendern($event)"
      />
    }
    <app-vw-ungespeichert [bereich]="this" />
  `,
  styles: `
    :host {
      display: block;
    }

    .vw-auswahllisten__fehler {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.75rem;
      padding: 1.25rem 1.5rem;
      border: 1px solid var(--to-border);
      border-radius: 0.75rem;
      background: var(--to-card);
      color: var(--to-red-fg);
      font-size: 0.875rem;
    }

    .vw-auswahllisten__erneut {
      height: 2.125rem;
      padding: 0 0.75rem;
      border: 1px solid var(--to-border);
      border-radius: 0.5rem;
      background: var(--to-card);
      color: var(--to-text-body);
      font: inherit;
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid var(--to-focus-ring);
        outline-offset: 2px;
      }
    }
  `,
})
export class VwAuswahllistenComponent implements VerwaltungBereich {
  private readonly api = inject(SelectionListDefaultService);
  private readonly store = inject(VerwaltungStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly zustand = signal<LadeZustand>('laedt');
  readonly feldFehler = signal<FeldFehler | null>(null);
  private readonly gespeichert = signal<SelectionListDefault[]>([]);
  private readonly entwuerfe = signal<Partial<Record<SelectionListKey, SelectionListWert[]>>>({});

  readonly listen = computed(() =>
    this.gespeichert().map((liste) => ({ ...liste, werte: this.entwuerfe()[liste.listKey] ?? liste.werte })),
  );

  private readonly geaendert = computed(() =>
    this.gespeichert().filter((liste) => {
      const entwurf = this.entwuerfe()[liste.listKey];
      return entwurf !== undefined && !gleicheWerte(entwurf, liste.werte);
    }),
  );

  readonly hatUngespeicherteAenderungen = computed(() => this.geaendert().length > 0);

  readonly kannSpeichern = computed(() => this.listen().every((l) => listeGueltig(l.listKey, l.werte)));

  constructor() {
    this.laden();
    this.store.bereichAnmelden(this);
    this.destroyRef.onDestroy(() => this.store.bereichAbmelden(this));
  }

  laden(): void {
    this.zustand.set('laedt');
    this.api
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (listen) => {
          this.gespeichert.set(listen);
          this.zustand.set('bereit');
        },
        error: () => this.zustand.set('fehler'),
      });
  }

  werteAendern({ listKey, werte }: WerteAenderung): void {
    this.entwuerfe.update((e) => ({ ...e, [listKey]: werte }));
    if (this.feldFehler()?.listKey === listKey) this.feldFehler.set(null);
  }

  speichern(): Observable<void> {
    this.feldFehler.set(null);
    return from(this.geaendert()).pipe(
      concatMap((liste) => this.listeSpeichern(liste)),
      toArray(),
      map(() => undefined),
      catchError((err: unknown) => (err instanceof Abgelehnt ? EMPTY : throwError(() => err))),
    );
  }

  verwerfen(): void {
    this.entwuerfe.set({});
    this.feldFehler.set(null);
  }

  private listeSpeichern(liste: SelectionListDefault): Observable<SelectionListDefault> {
    const entwurf = this.entwuerfe()[liste.listKey]!;
    const werte = entwurf.map((w) => ({ ...w, value: w.value.trim() }));
    const anfrage =
      gleicheWerte(werte, liste.standard) && liste.angepasst
        ? this.api.reset(liste.listKey).pipe(map(() => ({ ...liste, angepasst: false, werte: liste.standard })))
        : this.api.replace(liste.listKey, werte);
    return anfrage.pipe(
      tap((neu) => this.uebernehmen(neu, entwurf)),
      catchError((err: unknown) => {
        const fehler = this.feldFehlerAus(liste.listKey, err);
        if (!fehler) return throwError(() => err);
        this.feldFehler.set(fehler);
        return throwError(() => new Abgelehnt());
      }),
    );
  }

  private uebernehmen(neu: SelectionListDefault, gesendet: SelectionListWert[]): void {
    this.gespeichert.update((listen) => listen.map((l) => (l.listKey === neu.listKey ? neu : l)));
    if (this.entwuerfe()[neu.listKey] !== gesendet) return;
    this.entwuerfe.update((entwuerfe) => {
      const rest = { ...entwuerfe };
      delete rest[neu.listKey];
      return rest;
    });
  }

  private feldFehlerAus(listKey: SelectionListKey, err: unknown): FeldFehler | null {
    if (!(err instanceof HttpErrorResponse) || err.status !== 400) return null;
    const body = err.error as { code?: string; field?: string } | null;
    if (!body?.code || !body.field) return null;
    const index = FELD_INDEX.exec(body.field);
    return { listKey, index: index ? Number(index[1]) : null, code: body.code };
  }
}
